import { memo, useCallback } from "react"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { enqueueSnackbar } from "notistack"
import { Grid, Stack, Typography } from "@mui/material"

import { ProductGroups, ProductsTableItem } from "../../../../../../modules/merchant/admin/products/productsTypes"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"

import { deleteProductFavorite, saveProductFavorite } from "../../../createOrderApi"
import { DeliveryTimesField, DynamicConfigKey } from "../../form/formTypes"
import { Product } from "./Product"
import { getProductQuantityFromDeliveries } from "./helpers"
import { checkProductStock } from "../../../../productAvailability/stockHelpers"
import { UserDetails } from "../../../../../../modules/nonAdminUser/nonAdminUserApi"

const useStyles = makeStyles()((theme) => ({
    container: {
        flexGrow: "1",
        padding: "1.5rem",
        background: theme.palette.tailwind.slate[100],
        minHeight: 0,
        overflowY: "auto",
    },
    emptyWrap: {
        textAlign: "center",
        padding: "2rem 1rem",
        fontStyle: "italic",
        color: theme.palette.tailwind.slate[400],
    },
    disabledContent: {
        pointerEvents: "none",
        position: "relative",
        overflowY: "initial",
    },
}))

export type ShopFiltersTypes = Readonly<{
    isFavorite: boolean
    search: string
}>

type ProductContainerProps = Readonly<{
    list?: ProductGroups[]
    searchRegex?: RegExp
    isListView: boolean
    isFavoriteFilter: boolean
    isRefundOrder: boolean
    userId: number
    isEditMode?: boolean
}>

export const ProductContainer = memo(
    ({ list, searchRegex, isListView, isFavoriteFilter, isRefundOrder = false, userId, isEditMode }: ProductContainerProps) => {
        const { classes, cx } = useStyles()
        const { l10n } = useAppLocalization()

        const [{ value: deliveries }, , deliveriesHelpers] = useField<DeliveryTimesField>(
            DynamicConfigKey.MULTIPLE_DELIVERIES,
        )

        const { mutate: callSaveProductFavorite } = useApiMutation(saveProductFavorite, {
            async onSuccess(_, _data, context) {
                await context.queryClient.invalidateQueries({ queryKey: [UserDetails.USER_PRODUCTS_LIST] })
            },
        })

        const { mutate: callDeleteProductFavorite } = useApiMutation(deleteProductFavorite, {
            async onSuccess(_, _data, context) {
                await context.queryClient.invalidateQueries({ queryKey: [UserDetails.USER_PRODUCTS_LIST] })
            },
        })

        const addToBasket = useCallback(
            (product: ProductsTableItem, quantity: number) => {
                const activeTime = deliveries.activeTime

                if (!activeTime) {
                    enqueueSnackbar(l10n.getString("create-order-delivery-times-empty-error"), { variant: "error" })
                    return
                }

                if (quantity > 0) {
                    const stockCheck = checkProductStock(product.stock, quantity)
                    if (!stockCheck.allowed) {
                        const messageKey =
                            stockCheck.reason === "daily-limit"
                                ? "product-daily-limit-exceeded-snackbar"
                                : "product-stock-limit-exceeded-snackbar"
                        enqueueSnackbar(l10n.getString(messageKey), { variant: "warning" })
                        return
                    }
                }

                const updatedDeliveries = {
                    ...deliveries,
                    deliveryTimes: deliveries.deliveryTimes.map((delivery) => {
                        if (delivery.time === activeTime) {
                            const existingIndex = delivery.orderLines.findIndex(
                                (item) => item.id === product.id && !item.isExtraService,
                            )
                            let newOrderLines = [...delivery.orderLines]

                            if (quantity > 0) {
                                if (existingIndex !== -1) {
                                    newOrderLines[existingIndex] = { ...newOrderLines[existingIndex], quantity }
                                } else {
                                    newOrderLines = [...newOrderLines, { ...product, quantity }]
                                }
                            } else {
                                newOrderLines = newOrderLines.filter(
                                    (item) => !(item.id === product.id && !item.isExtraService),
                                )
                            }

                            return { ...delivery, orderLines: newOrderLines }
                        }
                        return delivery
                    }),
                }

                deliveriesHelpers.setValue(updatedDeliveries)
            },
            [deliveries, deliveriesHelpers, list],
        )

        const handleFavoriteProduct = useCallback(
            (productId: number, isFavorite: boolean): Promise<boolean> =>
                new Promise((resolve) => {
                    const onSuccess = () => resolve(true)
                    const onError = () => resolve(false)

                    if (isFavorite) {
                        callSaveProductFavorite(
                            { id: productId, productType: "MEETING_SERVICE" },
                            { onSuccess, onError },
                        )
                    } else {
                        callDeleteProductFavorite({ productId }, { onSuccess, onError })
                    }
                }),
            [callSaveProductFavorite, callDeleteProductFavorite],
        )

        const getProductQuantity = useCallback(
            (productId: number) => getProductQuantityFromDeliveries(deliveries, productId),
            [deliveries],
        )

        return (
            <Stack gap="2rem" className={cx(classes.container, { [classes.disabledContent]: isRefundOrder })}>
                {list && list.length > 0 ? (
                    list.map((group) => {
                        return (
                            <Stack gap="1.5rem" key={group.id}>
                                <Typography variant="h6">{group?.name}</Typography>
                                <Grid container gap="1.5rem" alignItems="stretch">
                                    {group?.products.map((product) => (
                                        <Product
                                            key={product.id}
                                            product={product}
                                            handleFavoriteProduct={handleFavoriteProduct}
                                            addToBasket={addToBasket}
                                            quantity={getProductQuantity(product.id)}
                                            searchRegex={searchRegex}
                                            isListView={isListView}
                                            userId={userId}
                                            isEditMode={isEditMode}
                                        />
                                    ))}
                                </Grid>
                            </Stack>
                        )
                    })
                ) : (
                    <Stack gap=".75rem" className={classes.emptyWrap}>
                        {isFavoriteFilter ? (
                            <>
                                <LocalizedStrict id="create-order-products-empty-favorites-title">
                                    <Typography variant="h6">You do not have any favorite items</Typography>
                                </LocalizedStrict>
                                <LocalizedStrict id="create-order-products-empty-favorites-description">
                                    <Typography>
                                        You can add favorite items so that you can complete an order more quickly. Add a
                                        favorite item by clicking on the heart icon found on the product card
                                    </Typography>
                                </LocalizedStrict>
                            </>
                        ) : (
                            <LocalizedStrict id="create-order-products-empty">
                                <Typography variant="h6">No results</Typography>
                            </LocalizedStrict>
                        )}
                    </Stack>
                )}
            </Stack>
        )
    },
)
